import { gql } from "graphql-request";
import { getGraphQLClientInstance } from "../clients/clientFactory";
import KeycloakUserService from "./KeycloakUserService";

/**
 * UsersService graphql endpoints
 */
export class UsersService {
  /**
   * To call the get user information endpoint
   */
  async getUser(id: string) {
    const query = gql`
      query ($id: String!) {
        userById(id: $id) {
          firstName
          lastName
          username
          email
          userRoles
        }
      }
    `;

    const { userById } = await getGraphQLClientInstance({ secured: true }).request(query, {
      id: id
    });

    return userById;
  }

  /**
   * To call the get user client roles endpoint
   */
  async getUserClientRoleMappings(id: string) {
    const query = gql`
      query ($id: String!) {
        userRoleMappingsByUserId(userId: $id, clientRole: true) {
          roleId
          roleInfo {
            name
            client
            clientInfo {
              clientId
            }
          }
        }
      }
    `;

    const { userRoleMappingsByUserId } = await getGraphQLClientInstance({ secured: true }).request(query, {
      id: id
    });

    return userRoleMappingsByUserId;
  }

  /**
   * To call the search users endpoint
   */
  async searchUsers(q: string) {
   const groupId = KeycloakUserService.getGroupId();
    const query = gql`
      query ($q: String!
             $groupId: String! ) {
        usersByEmailOrUsername(q: $q, groupId : $groupId) {
          id
          username
          email
          firstName
          lastName
          emailVerified
          availableApps
          userRoles
        }
      }
    `;

    const { usersByEmailOrUsername } = await getGraphQLClientInstance({ secured: true }).request(query, {
      q: q,
      groupId: groupId
    });

    return usersByEmailOrUsername;
  }

  /**
   * To call the create user endpoint
   */
  async createUser({
    username,
    firstName,
    lastName,
    email,
    password,
    realmRoles,
    clientIDs,
    clientRolesID,
    clientRolesName
  }: {
    username: string;
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    realmRoles: string[];
    clientIDs: string[];
    clientRolesID: string[];
    clientRolesName: string[];
  }) {
    const groupId = KeycloakUserService.getGroupId();
    const mutation = gql`
      mutation (
        $username: String!
        $firstName: String!
        $lastName: String!
        $email: String!
        $password: String!
        $realmRoles: [String!]!
        $clientIDs: [String!]!
        $clientRolesID: [String!]!
        $clientRolesName: [String!]!
        $groupId: String!
      ) {
        createUser(
          createUserInput: {
            username: $username
            firstName: $firstName
            lastName: $lastName
            email: $email
            password: $password
            realmRoles: $realmRoles
            clientIDs: $clientIDs
            clientRolesID: $clientRolesID
            clientRolesName: $clientRolesName
            groupId: $groupId
          }
        ) {
          id
          username
          firstName
          lastName
          email
        }
      }
    `;

    const { createUser } = await getGraphQLClientInstance({ secured: true }).request(mutation, {
      username,
      firstName,
      lastName,
      email,
      password,
      realmRoles,
      clientIDs,
      clientRolesID,
      clientRolesName,
      groupId
    });

    return createUser;
  }

  /**
   * To call the delete user endpoint
   */
  async deleteUser(id: string) {
    const mutation = gql`
      mutation ($id: String!) {
        deleteUser(id: $id)
      }
    `;

    const { deleteUser } = await getGraphQLClientInstance({ secured: true }).request(mutation, {
      id: id
    });

    return deleteUser;
  }

  /**
   * To call the update user information endpoint
   */
  async updateUser({ id, firstName, lastName }: { id: string; firstName: string; lastName: string }) {
    const mutation = gql`
      mutation ($id: String!, $firstName: String!, $lastName: String!) {
        updateUser(updateUserInput: { id: $id, firstName: $firstName, lastName: $lastName })
      }
    `;

    const { updateUser } = await getGraphQLClientInstance({ secured: true }).request(mutation, {
      id: id,
      firstName: firstName,
      lastName: lastName
    });

    return updateUser;
  }

  /**
   * To call the update user profile photo endpoint
   */
  async updateAvatar({ file, userid }: { file: File; userid: string }) {
    const mutation = gql`
      mutation ($file: Upload!, $userid: String!) {
        updateAvatar(file: $file, userid: $userid)
      }
    `;

    const { updateAvatar } = await getGraphQLClientInstance({ secured: true }).request(mutation, {
      file: file,
      userid: userid
    });

    return updateAvatar;
  }
}

export const usersService = new UsersService();
